// src/components/MapView/CompetitorMarkers.jsx
import React, { useEffect, useState } from "react";
import { CircleMarker, Tooltip, Popup, useMap } from "react-leaflet";
import { useSelector } from "react-redux";
import {
  selectActiveCompetitors,
  selectCompetitorsLoading,
} from "../../store/dataSlice";
import { selectCompetitorsVisible } from "../../store/mapSlice";
import { selectActiveGeographyType } from "../../store/configSlice";

/**
 * Custom pane name for competitor markers — z-index 500 sits above
 * overlayPane (400, where GeoJSON polygons render) but below
 * markerPane (600) and tooltipPane (650).
 */
const COMPETITORS_PANE = "competitorsPane";
const COMPETITORS_PANE_Z = 500;

/**
 * Competitor pin style — teal circle, distinct from all tier colors.
 * Teal (#00897b) is absent from the green→orange→red→gray tier spectrum.
 */
const MARKER_OPTIONS = {
  radius: 6,
  fillColor: "#00897b",
  fillOpacity: 0.9,
  color: "#ffffff",
  weight: 2,
  opacity: 1,
};

const MARKER_HOVER_OPTIONS = {
  radius: 8,
  fillColor: "#00695c",
  fillOpacity: 1,
  color: "#ffffff",
  weight: 2,
  opacity: 1,
};

/**
 * Renders competitor locations as circle markers on the Leaflet map.
 * Only renders when the competitors toggle is active and data is loaded.
 *
 * Uses CircleMarker with Tooltip (hover) instead of Popup (click).
 * Renders in a custom pane above GeoJSON polygons.
 */
const CompetitorMarkers = () => {
  const map = useMap();
  const [paneReady, setPaneReady] = useState(false);

  const visible = useSelector(selectCompetitorsVisible);
  const competitors = useSelector(selectActiveCompetitors);
  const loading = useSelector(selectCompetitorsLoading);
  const geographyType = useSelector(selectActiveGeographyType);

  // Create custom pane once
  useEffect(() => {
    if (map && !paneReady) {
      if (!map.getPane(COMPETITORS_PANE)) {
        const pane = map.createPane(COMPETITORS_PANE);
        pane.style.zIndex = COMPETITORS_PANE_Z;
      }
      setPaneReady(true);
    }
  }, [map, paneReady]);

  if (!visible || !competitors.length || !paneReady) return null;

  const geoField = geographyType === "BG" ? "blockGroup" : "zcta";
  const geoLabel = geographyType === "BG" ? "Block Group" : "ZIP Code";

  return (
    <>
      {competitors.map((comp) => (
        <CircleMarker
          key={comp.placeId}
          center={[comp.lat, comp.lng]}
          pathOptions={{ ...MARKER_OPTIONS, pane: COMPETITORS_PANE }}
          eventHandlers={{
            click: (e) => {
              e.target.closeTooltip();
            },
            mouseover: (e) => {
              e.target.setStyle(MARKER_HOVER_OPTIONS);
              e.target.setRadius(MARKER_HOVER_OPTIONS.radius);
            },
            mouseout: (e) => {
              e.target.setStyle(MARKER_OPTIONS);
              e.target.setRadius(MARKER_OPTIONS.radius);
            },
          }}
        >
          <Tooltip
            direction="top"
            offset={[0, -8]}
            className="competitor-tooltip"
          >
            <div style={{ minWidth: 160, maxWidth: 260 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 1,
                  color: "#1a1a1a",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {comp.name}
              </div>
              <div
                style={{
                  color: "#555",
                  fontSize: 11,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {comp.address}
              </div>
              <div style={{ color: "#555", fontSize: 11 }}>{comp.city}</div>
              <div style={{ color: "#888", fontSize: 10, marginTop: 3 }}>
                {geoLabel}: {comp[geoField] || "—"}
              </div>
              {comp.website && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.3,
                    color: "#00897b",
                  }}
                >
                  <a
                    href={comp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    {comp.website
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, "")}
                  </a>
                </div>
              )}
            </div>
          </Tooltip>
          <Popup
            offset={[0, 0]}
            className="competitor-popup"
            closeButton={true}
          >
            <div style={{ width: 240 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  marginBottom: 1,
                  color: "#1a1a1a",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {comp.name}
              </div>
              <div
                style={{
                  color: "#555",
                  fontSize: 11,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {comp.address}
              </div>
              <div style={{ color: "#555", fontSize: 11 }}>{comp.city}</div>
              <div style={{ color: "#888", fontSize: 10, marginTop: 3 }}>
                {geoLabel}: {comp[geoField] || "—"}
              </div>
              {comp.website && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.3,
                    color: "#00897b",
                  }}
                >
                  <a
                    href={comp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    {comp.website
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, "")}
                  </a>
                </div>
              )}
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
};

export default CompetitorMarkers;
